// Content loader: reads /content/index.json (slug list) + each
// project's /content/projects/<slug>/project.json. All asset paths
// in a manifest are resolved relative to that project folder.
//
// Paths starting with `cdn:` are routed through `VITE_CDN_BASE`
// (when set at build time) so oversized files can be hosted on R2
// instead of bundled with the Pages deploy. With no env var the
// `cdn:` prefix transparently resolves to the same local path as
// any other relative asset, so dev still works.

const BASE = '/content';
// Public R2 bucket for oversized assets (videos, baked line file,
// PDFs) that can't fit on Pages' 25 MB single-file limit. The env
// override lets a fork/test deploy point elsewhere without editing
// code; when empty, prod still works.
const DEFAULT_CDN_BASE = 'https://pub-eaa5e8da6e614a79993882246a4aca33.r2.dev';
const CDN_BASE = (import.meta.env.VITE_CDN_BASE || DEFAULT_CDN_BASE).replace(/\/$/, '');

export async function loadSite() {
  const [site, index] = await Promise.all([
    fetchJSON(`${BASE}/site.json`),
    fetchJSON(`${BASE}/index.json`),
  ]);

  const projects = [];
  for (const slug of index) {
    try {
      const manifest = await fetchJSON(`${BASE}/projects/${slug}/project.json`);
      manifest.slug = slug;
      manifest.basePath = `${BASE}/projects/${slug}`;
      manifest.blocks = (manifest.blocks || []).map((b) => resolveBlock(b, manifest.basePath));
      projects.push(manifest);
    } catch (err) {
      console.warn(`[content] failed to load project "${slug}":`, err);
    }
  }
  return { site, projects };
}

// Resolve a single asset path. Handles three cases:
//  - absolute URL / root-relative path → returned as-is
//  - `cdn:<rest>` → CDN_BASE + rest in prod, basePath + rest in dev
//  - bare relative path → basePath + path
function resolvePath(p, basePath) {
  if (!p) return p;
  if (p.startsWith('cdn:')) {
    const rest = p.slice(4).replace(/^\//, '');
    return CDN_BASE ? `${CDN_BASE}/${rest}` : `${basePath}/${rest}`;
  }
  return isAbsolute(p) ? p : `${basePath}/${p}`;
}

function resolveBlock(block, basePath) {
  const r = (p) => resolvePath(p, basePath);
  const out = { ...block };
  if (out.src) out.src = r(out.src);
  if (out.srcMobile) out.srcMobile = r(out.srcMobile);
  if (out.folder) out.folder = r(out.folder);
  if (out.bakedFile) out.bakedFile = r(out.bakedFile);
  if (out.bakedFileMobile) out.bakedFileMobile = r(out.bakedFileMobile);
  if (out.bakedFilePreview) out.bakedFilePreview = r(out.bakedFilePreview);
  if (out.envHdr) out.envHdr = r(out.envHdr);
  if (out.poster) out.poster = r(out.poster);
  if (Array.isArray(out.images)) {
    out.images = out.images.map((i) =>
      typeof i === 'string' ? r(i) : { ...i, src: r(i.src) }
    );
  }
  if (Array.isArray(out.videos)) {
    out.videos = out.videos.map((i) =>
      typeof i === 'string'
        ? r(i)
        : { ...i, src: r(i.src), poster: r(i.poster) }
    );
  }
  if (Array.isArray(out.models)) {
    out.models = out.models.map((i) =>
      typeof i === 'string'
        ? { src: r(i) }
        : { ...i, src: r(i.src), envHdr: r(i.envHdr), poster: r(i.poster), texture: r(i.texture) }
    );
  }
  if (Array.isArray(out.parts)) {
    out.parts = out.parts.map((i) =>
      typeof i === 'string'
        ? { src: r(i) }
        : { ...i, src: r(i.src), texture: r(i.texture) }
    );
  }
  if (Array.isArray(out.items)) {
    out.items = out.items.map((i) => ({
      ...i,
      src: r(i.src),
      thumb: r(i.thumb),
    }));
  }
  return out;
}

function isAbsolute(p) {
  return /^(https?:)?\/\//.test(p) || p.startsWith('/');
}

async function fetchJSON(url) {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  return res.json();
}
